/**
 * Benefits in Kind (BIK) Data Hooks
 * 
 * React Query hooks for managing BIK data including:
 * - One-Time Benefits (company trips, long service awards)
 * - Accommodation BIK (dormitory)
 * - Motor Vehicle BIK
 * - Monthly BIK Records (aggregated)
 * - IR8A Appendix 8A Export
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import toast from '../utils/toast';

// =============================================================================
// ONE-TIME BENEFITS
// =============================================================================

/**
 * Fetch all one-time benefits
 * @param {Object} filters - Optional filters (year, staff_id, benefit_type, status)
 */
export const useOneTimeBenefits = (filters = {}) => {
  return useQuery({
    queryKey: ['bik', 'one-time-benefits', filters],
    queryFn: async () => {
      const params = {};
      if (filters.year) params.year = filters.year;
      if (filters.staff_id) params.staff_id = filters.staff_id;
      if (filters.benefit_type) params.benefit_type = filters.benefit_type;
      if (filters.status) params.status = filters.status;
      
      const response = await api.get('/confidential/one-time-benefits/', { params });
      return response.data.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetch a single one-time benefit
 * @param {string} id - Benefit ID
 */
export const useOneTimeBenefit = (id) => {
  return useQuery({
    queryKey: ['bik', 'one-time-benefits', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await api.get(`/confidential/one-time-benefits/${id}/`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Create a new one-time benefit
 */
export const useCreateOneTimeBenefit = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/confidential/one-time-benefits/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bik', 'one-time-benefits'] });
      toast.success('One-time benefit created successfully');
    },
    onError: (error) => {
      console.error('Error creating one-time benefit:', error);
      toast.error(error.response?.data?.detail || 'Failed to create benefit');
    },
  });
};

/**
 * Update a one-time benefit
 */
export const useUpdateOneTimeBenefit = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(`/confidential/one-time-benefits/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bik', 'one-time-benefits'] });
      toast.success('Benefit updated successfully');
    },
    onError: (error) => {
      console.error('Error updating one-time benefit:', error);
      toast.error(error.response?.data?.detail || 'Failed to update benefit');
    },
  });
};

/**
 * Delete a one-time benefit
 */
export const useDeleteOneTimeBenefit = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/confidential/one-time-benefits/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bik', 'one-time-benefits'] });
      toast.success('Benefit deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting one-time benefit:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete benefit');
    },
  });
};

// Benefit type options for forms (must match OneTimeBenefit.BENEFIT_TYPE_CHOICES).
// Grouped by IRAS concession treatment so HR understands the tax impact.
export const BENEFIT_TYPE_OPTIONS = [
  // Fully taxable (no IRAS concession)
  { value: 'incentive_trip', label: 'Company Incentive Trip' },
  { value: 'entertainment', label: 'Entertainment Benefit' },
  { value: 'other', label: 'Other Benefit' },
  // Threshold concessions (IRAS cliff)
  { value: 'gift', label: 'Gift/Prize (festive/occasion)' },
  { value: 'long_service_award', label: 'Long Service Award' },
  { value: 'service_award', label: 'Service / Recognition Award' },
  { value: 'staff_discount', label: 'Staff Discount' },
  // Fully exempt by type
  { value: 'bereavement', label: 'Bereavement Gift' },
  { value: 'medical_insurance', label: 'Group Medical/Hospitalisation Insurance' },
  { value: 'outpatient_dental', label: 'Outpatient / Dental Treatment' },
  { value: 'training', label: 'Training / Course / Exam Fees' },
  { value: 'work_equipment', label: 'Work Equipment (laptop/phone for business)' },
  { value: 'group_outing', label: 'Group Outing / Dinner & Dance' },
  { value: 'ot_reimbursement', label: 'Overtime Meal / Transport Reimbursement' },
  { value: 'commercial_vehicle_use', label: 'Incidental Private Use of Commercial Vehicle' },
];

// Benefit types whose IRAS exemption is conditional on being available to all staff
export const ALL_STAFF_CONDITIONAL_TYPES = [
  'staff_discount', 'medical_insurance', 'outpatient_dental', 'group_outing',
];

// =============================================================================
// ACCOMMODATION BIK (DORMITORY)
// =============================================================================

/**
 * Fetch accommodation BIK data for an employee
 * @param {string} staffId - Employee staff ID
 * @param {number} year - Tax year
 */
export const useAccommodationBIK = (staffId, year) => {
  return useQuery({
    queryKey: ['bik', 'accommodation', staffId, year],
    queryFn: async () => {
      if (!staffId || !year) return null;
      const response = await api.get('/confidential/accommodation-bik/calculate/', {
        params: { staff_id: staffId, year }
      });
      return response.data;
    },
    enabled: !!staffId && !!year,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Fetch the month-by-month accommodation BIK breakdown for one employee.
 * Powers the per-worker detail modal in the Accommodation tab.
 * @param {string} staffId - Employee staff ID
 * @param {number} year - Tax year
 */
export const useAccommodationBIKDetail = (staffId, year) => {
  return useQuery({
    queryKey: ['bik', 'accommodation', 'detail', staffId, year],
    queryFn: async () => {
      if (!staffId || !year) return null;
      const response = await api.get('/confidential/accommodation-bik/employee-detail/', {
        params: { staff_id: staffId, year }
      });
      return response.data;
    },
    enabled: !!staffId && !!year,
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Fetch accommodation BIK summary for all employees
 * @param {number} year - Tax year
 */
export const useAccommodationBIKSummary = (year) => {
  return useQuery({
    queryKey: ['bik', 'accommodation', 'summary', year],
    queryFn: async () => {
      if (!year) return null;
      const response = await api.get('/confidential/accommodation-bik/summary/', {
        params: { year }
      });
      return response.data;
    },
    enabled: !!year,
    staleTime: 10 * 60 * 1000,
  });
};

// =============================================================================
// MOTOR VEHICLE BIK
// =============================================================================

/**
 * Fetch motor vehicle BIK data for an employee
 * @param {string} staffId - Employee staff ID
 * @param {number} year - Tax year
 */
export const useVehicleBIK = (staffId, year) => {
  return useQuery({
    queryKey: ['bik', 'vehicle', staffId, year],
    queryFn: async () => {
      if (!staffId || !year) return null;
      const response = await api.get('/confidential/vehicle-bik/calculate/', {
        params: { staff_id: staffId, year }
      });
      return response.data;
    },
    enabled: !!staffId && !!year,
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Fetch motor vehicle BIK summary for all employees
 * @param {number} year - Tax year
 */
export const useVehicleBIKSummary = (year) => {
  return useQuery({
    queryKey: ['bik', 'vehicle', 'summary', year],
    queryFn: async () => {
      if (!year) return null;
      const response = await api.get('/confidential/vehicle-bik/summary/', {
        params: { year }
      });
      return response.data;
    },
    enabled: !!year,
    staleTime: 10 * 60 * 1000,
  });
};

// =============================================================================
// MONTHLY BIK RECORDS (AGGREGATED)
// =============================================================================

/**
 * Fetch monthly BIK records
 * @param {Object} filters - Optional filters (year, staff_id, status)
 */
export const useMonthlyBIKRecords = (filters = {}) => {
  return useQuery({
    queryKey: ['bik', 'monthly-records', filters],
    queryFn: async () => {
      const params = { month: 0 }; // Annual records only
      if (filters.year) params.year = filters.year;
      if (filters.staff_id) params.staff_id = filters.staff_id;
      if (filters.status) params.status = filters.status;
      
      const response = await api.get('/confidential/monthly-bik-records/', { params });
      return response.data.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Live BIK working view (compute-on-read; frozen snapshots overlaid).
 * Returns { rows, category_totals, total_bik, employee_count, status_counts }.
 * @param {number} year - Tax year
 * @param {string} departmentId - Optional department filter
 */
export const useLiveBIKRoster = (year, departmentId = null) => {
  return useQuery({
    queryKey: ['bik', 'live-roster', year, departmentId],
    queryFn: async () => {
      const params = { year };
      if (departmentId) params.department_id = departmentId;
      const response = await api.get('/confidential/monthly-bik-records/live/', { params });
      return response.data;
    },
    enabled: !!year,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Live BIK detail (breakdown + per-vehicle derivation) for one employee.
 * @param {string} staffId - Employee staff ID
 * @param {number} year - Tax year
 */
export const useLiveBIKDetail = (staffId, year) => {
  return useQuery({
    queryKey: ['bik', 'live-detail', staffId, year],
    queryFn: async () => {
      if (!staffId || !year) return null;
      const response = await api.get('/confidential/monthly-bik-records/live-detail/', {
        params: { staff_id: staffId, year },
      });
      return response.data;
    },
    enabled: !!staffId && !!year,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Freeze an employee's live BIK into the IR8A snapshot (verify or lock).
 */
export const useFreezeBIK = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ staffId, year, action }) => {
      const response = await api.post('/confidential/monthly-bik-records/freeze/', {
        staff_id: staffId,
        year,
        action: action || 'verify',
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bik'] });
      toast.success(variables.action === 'lock' ? 'BIK locked for IR8A' : 'BIK verified');
    },
    onError: (error) => {
      console.error('Error freezing BIK:', error);
      toast.error(error.response?.data?.error || 'Failed to freeze BIK record');
    },
  });
};

/**
 * Fetch a single monthly BIK record (full detail incl. parsed breakdown)
 * @param {string} id - MonthlyBIKRecord ID
 */
export const useBIKRecord = (id) => {
  return useQuery({
    queryKey: ['bik', 'monthly-records', 'detail', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await api.get(`/confidential/monthly-bik-records/${id}/`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch BIK records for a specific employee
 * @param {string} staffId - Employee staff ID
 */
export const useBIKRecordsForEmployee = (staffId) => {
  return useQuery({
    queryKey: ['bik', 'monthly-records', 'employee', staffId],
    queryFn: async () => {
      if (!staffId) return [];
      const response = await api.get('/confidential/monthly-bik-records/for_employee/', {
        params: { staff_id: staffId }
      });
      return response.data.records || [];
    },
    enabled: !!staffId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch BIK summary for a year
 * @param {number} year - Tax year
 * @param {string} departmentId - Optional department filter
 */
export const useBIKSummary = (year, departmentId = null) => {
  return useQuery({
    queryKey: ['bik', 'summary', year, departmentId],
    queryFn: async () => {
      if (!year) return null;
      const params = { year };
      if (departmentId) params.department_id = departmentId;
      
      const response = await api.get('/confidential/monthly-bik-records/summary/', { params });
      return response.data;
    },
    enabled: !!year,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Calculate BIK for employees (triggers Celery task)
 */
export const useCalculateBIK = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ year, staffId, departmentId, runAsync }) => {
      const data = { year };
      if (staffId) data.staff_id = staffId;
      if (departmentId) data.department_id = departmentId;
      // Per-employee recalcs default to synchronous so the UI can refresh
      // immediately; bulk recalcs stay async (Celery) unless told otherwise.
      if (runAsync !== undefined) {
        data.async = runAsync;
      } else if (staffId) {
        data.async = false;
      }
      
      const response = await api.post('/confidential/monthly-bik-records/calculate/', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bik'] });
      if (data.task_id) {
        toast.success('BIK calculation started in background');
      } else if (data.staff_id) {
        toast.success(`BIK recalculated for ${data.staff_id}`);
      } else {
        toast.success(`BIK calculated: ${data.success_count ?? data.processed_count ?? 0} employees processed`);
      }
    },
    onError: (error) => {
      console.error('Error calculating BIK:', error);
      toast.error(error.response?.data?.error || 'Failed to calculate BIK');
    },
  });
};

/**
 * Verify a BIK record
 */
export const useVerifyBIKRecord = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, notes }) => {
      const response = await api.post(`/confidential/monthly-bik-records/${id}/verify/`, { notes });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bik', 'monthly-records'] });
      toast.success('BIK record verified');
    },
    onError: (error) => {
      console.error('Error verifying BIK record:', error);
      toast.error(error.response?.data?.error || 'Failed to verify record');
    },
  });
};

/**
 * Lock a BIK record for IR8A
 */
export const useLockBIKRecord = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id) => {
      const response = await api.post(`/confidential/monthly-bik-records/${id}/lock/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bik', 'monthly-records'] });
      toast.success('BIK record locked for IR8A');
    },
    onError: (error) => {
      console.error('Error locking BIK record:', error);
      toast.error(error.response?.data?.error || 'Failed to lock record');
    },
  });
};

// =============================================================================
// IR8A APPENDIX 8A EXPORT
// =============================================================================

/**
 * Fetch IR8A Appendix 8A records
 * @param {Object} filters - Optional filters (year, status, staff_id)
 */
export const useIR8ARecords = (filters = {}) => {
  return useQuery({
    queryKey: ['bik', 'ir8a', 'records', filters],
    queryFn: async () => {
      const params = {};
      if (filters.year) params.year = filters.year;
      if (filters.status) params.status = filters.status;
      if (filters.staff_id) params.staff_id = filters.staff_id;
      
      const response = await api.get('/confidential/ir8a-appendix-8a/', { params });
      return response.data.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch a single IR8A record
 * @param {string} id - Record ID
 */
export const useIR8ARecord = (id) => {
  return useQuery({
    queryKey: ['bik', 'ir8a', 'records', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await api.get(`/confidential/ir8a-appendix-8a/${id}/`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch IR8A export summary
 * @param {number} year - Tax year
 * @param {string} departmentId - Optional department filter
 */
export const useIR8ASummary = (year, departmentId = null) => {
  return useQuery({
    queryKey: ['bik', 'ir8a', 'summary', year, departmentId],
    queryFn: async () => {
      if (!year) return null;
      const params = { year };
      if (departmentId) params.department_id = departmentId;
      
      const response = await api.get('/confidential/ir8a-appendix-8a/summary/', { params });
      return response.data;
    },
    enabled: !!year,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Generate IR8A Appendix 8A records from BIK data
 */
export const useGenerateIR8A = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ year, staffId, departmentId }) => {
      const data = { year };
      if (staffId) data.staff_id = staffId;
      if (departmentId) data.department_id = departmentId;
      
      const response = await api.post('/confidential/ir8a-appendix-8a/generate/', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bik', 'ir8a'] });
      if (data.staff_id) {
        // Single employee
        toast.success(`IR8A record generated for ${data.staff_id}`);
      } else {
        // Batch generation
        toast.success(`IR8A records generated: ${data.generated_count || 0} employees`);
      }
    },
    onError: (error) => {
      console.error('Error generating IR8A records:', error);
      toast.error(error.response?.data?.error || 'Failed to generate IR8A records');
    },
  });
};

/**
 * Verify an IR8A record
 */
export const useVerifyIR8ARecord = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, notes }) => {
      const response = await api.post(`/confidential/ir8a-appendix-8a/${id}/verify/`, { notes });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bik', 'ir8a'] });
      toast.success('IR8A record verified');
    },
    onError: (error) => {
      console.error('Error verifying IR8A record:', error);
      toast.error(error.response?.data?.error || 'Failed to verify record');
    },
  });
};

/**
 * Mark IR8A records as submitted to IRAS
 */
export const useSubmitIR8ARecords = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ recordIds, irasReference }) => {
      const response = await api.post('/confidential/ir8a-appendix-8a/mark_submitted/', {
        record_ids: recordIds,
        iras_reference: irasReference,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bik', 'ir8a'] });
      toast.success(`${data.updated_count} records marked as submitted`);
    },
    onError: (error) => {
      console.error('Error marking IR8A as submitted:', error);
      toast.error(error.response?.data?.error || 'Failed to update submission status');
    },
  });
};

/**
 * Preview the full IR8A myTax Portal bulk-upload dataset for a year.
 * Returns employee particulars + income/deductions + BIK, totals, data-quality
 * issues, and the number of 200-record files needed.
 * @param {number} year - Tax year
 */
export const useIR8AMyTaxPreview = (year) => {
  return useQuery({
    queryKey: ['bik', 'ir8a', 'mytax-preview', year],
    queryFn: async () => {
      if (!year) return null;
      const response = await api.get('/confidential/ir8a-appendix-8a/mytax_preview/', {
        params: { year },
      });
      return response.data;
    },
    enabled: !!year,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Download the IRAS myTax Portal "IR8A Import Template" (.xlsx, or .zip when
 * there are more than 200 employees) filled with the year's records.
 * @param {number} year - Tax year
 */
export const useExportIR8AMyTax = () => {
  return useMutation({
    mutationFn: async ({ year }) => {
      const response = await api.get('/confidential/ir8a-appendix-8a/export_mytax/', {
        params: { year },
        responseType: 'blob',
      });

      // Derive filename from Content-Disposition (xlsx vs zip).
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `IR8A_myTax_${year}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    },
    onSuccess: () => {
      toast.success('IR8A myTax template downloaded');
    },
    onError: (error) => {
      console.error('Error exporting IR8A myTax template:', error);
      const status = error.response?.status;
      if (status === 400) {
        toast.error('No employees with payroll income found for this year');
      } else {
        toast.error('Failed to export IR8A myTax template');
      }
    },
  });
};

/**
 * Export IR8A records to CSV
 * @param {number} year - Tax year
 * @param {boolean} includeZeroBIK - Include employees with zero BIK
 */
export const useExportIR8ACSV = () => {
  return useMutation({
    mutationFn: async ({ year, includeZeroBIK = false }) => {
      const response = await api.get('/confidential/ir8a-appendix-8a/export_csv/', {
        params: { year, include_zero_bik: includeZeroBIK },
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `IR8A_Appendix8A_${year}_YOA${year + 1}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return true;
    },
    onSuccess: () => {
      toast.success('IR8A CSV exported successfully');
    },
    onError: (error) => {
      console.error('Error exporting IR8A CSV:', error);
      toast.error('Failed to export IR8A CSV');
    },
  });
};

// =============================================================================
// BIK OVERVIEW (COMBINED STATISTICS)
// =============================================================================

// =============================================================================
// FLEET VEHICLES (Asset Inventory API)
// =============================================================================

/**
 * Fetch all fleet vehicles
 * @param {Object} filters - Optional filters
 */
export const useFleetVehicles = (filters = {}) => {
  return useQuery({
    queryKey: ['asset-inventory', 'fleet-vehicles', filters],
    queryFn: async () => {
      const params = { ...filters };
      const response = await api.get('/asset-inventory/fleet-vehicles/', { params });
      return response.data.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch BIK-relevant fleet vehicles (S plates assigned to employees)
 */
export const useBIKRelevantVehicles = () => {
  return useQuery({
    queryKey: ['asset-inventory', 'fleet-vehicles', 'bik-relevant'],
    queryFn: async () => {
      const response = await api.get('/asset-inventory/fleet-vehicles/bik_relevant/');
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch BIK reconciliation data (read-only, year-aware dashboard)
 *
 * @param {number} year - Tax year (default: current year)
 *
 * Returns:
 * - commercial_exempt: Commercial vehicles (G/GB plates) - BIK exempt
 * - private_with_bik: Private vehicles (S plates) with assignees in the selected year (prorated BIK)
 * - private_no_assignee: Private vehicles with no assignee in that year
 * - unknown_plates: Vehicles with unrecognized plate formats
 */
export const useBIKReconciliation = (year) => {
  const y = year != null && year !== '' ? Number(year) : new Date().getFullYear();
  return useQuery({
    queryKey: ['asset-inventory', 'fleet-vehicles', 'reconciliation', y],
    queryFn: async () => {
      const response = await api.get('/asset-inventory/fleet-vehicles/reconciliation/', {
        params: { year: y },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

// =============================================================================
// VEHICLE BIK ASSIGNMENT HOOKS - REMOVED
// =============================================================================
// 
// The following hooks have been removed as part of BIK simplification:
// - useVehicleBIKAssignments
// - useActiveVehicleBIKAssignments
// - useVehicleBIKAssignment
// - useCreateVehicleBIKAssignment
// - useUpdateVehicleBIKAssignment
// - useEndVehicleBIKAssignment
// - useDeleteVehicleBIKAssignment
//
// BIK is now calculated automatically from Asset Portal data.
// All private vehicles (S plates) use uniform 3/7 rule (42.86%).
// =============================================================================
